import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { CSSProperties, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";

import { ampli } from "../../ampli";
import { LOGIN_SECTION_ID } from "../../pages/contract/constants";
import { getBasicUserDetailsById } from "../../setup/api/services/user";
import {
  IUserInfoResponse,
  IUserInfoResponseData,
} from "../../setup/api/services/user/models";
import { PROFILE_PATH_PARAMS, ROUTES } from "../../setup/constants";
import {
  isLoggedIn,
  logOut,
  UserContext,
} from "../../setup/context/authContext";
import { AuthConfig } from "../../setup/context/config/authConfig";
import {
  EVENT_LOCAL_STORAGE_LOGIN,
  EVENT_LOCAL_STORAGE_UPDATE_MY_INFO,
} from "../../setup/context/events";
import Avatar, { AvatarSize } from "../common/Avatar";
import { DarkLogo, WhiteLogo } from "../common/logo";
import PrimaryButton from "../common/PrimaryButton";
import { ProfileInfoCard } from "../common/profileCard/profileInfoCard";
import Notification from "../notifications-listing";
import { NAVBAR_ITEMS, whatsappNumber } from "./constants";
import styles from "./styles.module.scss";

export const NavBar = () => {
  const userInfo = AuthConfig.getUserInfo();
  const userId = AuthConfig.getUserInfo()?.id;
  const pathName = window.location.pathname;
  const [isAuth, setIsAuth] = useState<boolean>(isLoggedIn());

  useEffect(() => {
    window.addEventListener(EVENT_LOCAL_STORAGE_LOGIN, () => {
      setIsAuth(isLoggedIn());
    });
  }, []);

  const fetchUserBasicInfo = useCallback(async () => {
    if (userId) {
      try {
        const response: IUserInfoResponse = await getBasicUserDetailsById(
          userId
        );
        if (response) {
          setUserDetials(response?.data);
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
      }
    }
  }, [userId]);

  useEffect(() => {
    window.addEventListener(EVENT_LOCAL_STORAGE_UPDATE_MY_INFO, () => {
      fetchUserBasicInfo();
    });
  }, [fetchUserBasicInfo]);

  const isContractLocation = () => {
    return pathName.includes("contract");
  };
  const [userDetails, setUserDetials] = useState<
    IUserInfoResponseData | undefined
  >(undefined);
  const navigate = useNavigate();
  const [pages, setPages] = useState<{ label: string; route: string }[]>();
  const [navBarStyle, setNavBarStyle] = useState<CSSProperties>({
    background: "#FFFFFF",
  });
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();

  const location = useLocation();

  const [anchorNotificationEl, setAnchorNotificationEl] =
    useState<HTMLDivElement | null>(null);

  const handleNotificationPopoverOpen = useCallback((event: any) => {
    setAnchorNotificationEl(event.currentTarget);
  }, []);

  const handlePopoverClose = () => {
    setAnchorNotificationEl(null);
  };

  useEffect(() => {
    if (userId) fetchUserBasicInfo().catch(console.error);
  }, [fetchUserBasicInfo, userId]);

  const handleOpenNavMenu = useCallback((event: any) => {
    setAnchorElNav(event.currentTarget);
  }, []);

  const handleCloseNavMenu = (route: string) => {
    setAnchorElNav(null);
    navigate(route);
  };
  useEffect(() => {
    if (isAuth === true) {
      setPages(NAVBAR_ITEMS);
      setNavBarStyle({
        background: "#1B242D",
        color: "#FFFFFF",
      });
    } else {
      setPages([]);
      setNavBarStyle({
        background: "#FFFFFF",
        justifyContent: "space-between",
      });
    }
  }, [isAuth]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = useCallback((event: any) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onProfileInfoClick = () => {
    navigate(`${ROUTES.PROFILE}?path=${PROFILE_PATH_PARAMS.ME}`);
    handleMenuClose();
  };
  const onChangePinCodeClick = () => {
    navigate(`${ROUTES.PROFILE}?path=${PROFILE_PATH_PARAMS.PIN_CODE}`);
    handleMenuClose();
  };
  const onSignOutclick = () => {
    try {
      ampli?.client?.setUserId(null);
      ampli?.client?.setUserProperties({ userId: null });
    } catch (error) {
      console.error(error);
    }
    logOut();
    handleMenuClose();
  };
  const onLoginClick = () => {
    const elmntToView = document.getElementById(LOGIN_SECTION_ID);
    elmntToView && elmntToView.scrollIntoView();
  };
  const menuId = "primary-account-menu";
  const renderMenu = (
    <Menu
      dir="rtl"
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        width: { md: "339px", xs: "100vw" },
        maxWidth: { md: "339px", xs: "100vw" },
        top: { md: "inherit", xs: "26px !important" },
        left: { md: "10% !important", xs: "0px !important" },
        right: { md: "10% !important", xs: "0px !important" },
      }}
      PaperProps={{
        sx: {
          width: { md: "282px", xs: "100vw" },
          maxWidth: { md: "282px", xs: "100vw" },
          top: { md: "inherit", xs: "46px !important" },
          left: { md: "10% !important", xs: "0px !important" },
          right: { md: "10% !important", xs: "0px !important" },
          borderRadius: "12px",
        },
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}>
      <div onClick={onProfileInfoClick}>
        <div className={styles.user_popover}>
          <ProfileInfoCard
            name={userDetails?.profile?.fullName || ""}
            phoneNumber={userInfo?.phoneNumber || ""}
            profile={userDetails?.profile?.photoUrl || ""}
            isNavBar={true}
            avatarSize={AvatarSize.MEDIUM}
          />
          <div className={styles.popover_button_wrapper}>
            <PrimaryButton id={`navbar_profile_button`}>
              {" "}
              {t("lbl_show_you_profile")}
            </PrimaryButton>
          </div>
        </div>
      </div>
      <div className={styles.divider} />
      <div
        id={`navbar_profile_pin_code`}
        className={styles.menu_item}
        onClick={onChangePinCodeClick}>
        <div className={styles.ml1}>
          <img alt="img" src="/assets/images/svg/key.svg" />
        </div>
        <p className={"p1"}>{t("lbl_profile_items_change_pin_code")}</p>
      </div>
      <div className={styles.divider} />
      <div
        id={`navbar_profile_logout`}
        className={styles.menu_item}
        onClick={onSignOutclick}>
        <div className={styles.ml1}>
          <img alt="img" src="/assets/images/svg/logout.svg" />
        </div>
        <p className={"p1"}>{t("lbl_profile_btn_logout")}</p>
      </div>
    </Menu>
  );
  const notificationElementId = "notification-el";

  const footerNavigation = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate]
  );

  const renderFooter = () => (
    <div className={styles.footerMenu}>
      {NAVBAR_ITEMS?.map((page) => {
        return (
          <div
            key={page?.route}
            id={`navbar_${page.label}`}
            onClick={() => footerNavigation(page.route)}
            className={classNames(styles.footerItems)}>
            <div
              className={classNames(
                styles.footerItem,
                location?.pathname === page.route
                  ? styles.item_selected
                  : styles.item_not_selected
              )}>
              {page.route === ROUTES.HOME && (
                <img
                  src={
                    location?.pathname === page.route
                      ? "/assets/images/svg/homeTabSelected.svg"
                      : "/assets/images/svg/homeTab.svg"
                  }
                />
              )}
              {page.route === ROUTES.CONTRACTS_LIST && (
                <img
                  src={
                    location?.pathname === page.route
                      ? "/assets/images/svg/transactionTabSelected.svg"
                      : "/assets/images/svg/transactionTab.svg"
                  }
                />
              )}
              {page.route === ROUTES.PAYMENTS && (
                <img
                  src={
                    location?.pathname === page.route
                      ? "/assets/images/svg/walletTabSelected.svg"
                      : "/assets/images/svg/walletTab.svg"
                  }
                />
              )}
              <p
                className={classNames(
                  "p2",
                  location?.pathname === page.route ? "primary" : "g400"
                )}>
                {t(page.label)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <UserContext.Consumer>
      {({ isLoggedIn }) => (
        <>
          <Notification
            elementId={notificationElementId}
            anchorElNotification={anchorNotificationEl}
            handlePopoverClose={handlePopoverClose}
          />
          <div
            className={classNames(
              styles.navbar,
              !isAuth ? styles.navbar_light : styles.navbar_dark
            )}>
            <div className={styles.navContainer}>
              <div className={styles.toolBar}>
                {isAuth ? (
                  <WhiteLogo />
                ) : (
                  <a
                    href={process.env.REACT_APP_WAFFY_DASHBOARD_URL}
                    target="_blank"
                    rel="noreferrer">
                    <DarkLogo />
                  </a>
                )}

                <Box
                  sx={{
                    flexGrow: 1,
                    display: { xs: "none", md: "flex", marginRight: "16%" },
                  }}>
                  {pages?.map((page) => {
                    return (
                      <Button
                        key={t(page.label)}
                        id={`navbar_${page.label}`}
                        onClick={() => {
                          handleCloseNavMenu(page.route);
                        }}
                        sx={{
                          my: 2,
                          color: "#FFFFFF",
                          display: "block",
                          borderBottom:
                            location?.pathname === page.route
                              ? `0.2rem solid #078DE7`
                              : "",
                          borderRadius: 0,
                          margin: "0 0.5rem",
                          fontWeight:
                            location?.pathname === page.route ? 700 : 400,
                          fontSize:
                            location?.pathname === page.route
                              ? "1.2rem"
                              : "1.1rem",
                        }}>
                        <h4 className={"white"}>{t(page.label)}</h4>
                      </Button>
                    );
                  })}
                </Box>
                {isAuth ? (
                  <div className={styles["mobile_container"]}>
                    <a
                      className={styles.mobile_wrapper}
                      target="blank"
                      id="navbar_whatsapp"
                      href={`https://wa.me/${whatsappNumber}`}>
                      <div className={styles.whatsappLink}>
                        <img src="/assets/images/svg/whatsappWhite.svg" />
                        <p dir="ltr" className={styles.mobileNumber_white}>
                          {t("lbl_customer_service")}
                        </p>
                      </div>
                    </a>
                    <div
                      id={`navbar_notification`}
                      onClick={handleNotificationPopoverOpen}
                      className={styles["notification_box"]}>
                      <img src="/assets/images/svg/notification.svg" />
                    </div>
                    <div
                      className={styles.profile_box}
                      onClick={handleProfileMenuOpen}>
                      <Avatar
                        src={userDetails?.profile?.photoUrl}
                        avatarSize={AvatarSize.SMALLER}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <a
                      className={styles.mobile_blue_wrapper}
                      target="blank"
                      id="navbar_whatsapp"
                      href={`https://wa.me/${whatsappNumber}`}>
                      <div className={styles.whatsappLink}>
                        <img src="/assets/images/svg/whatsapp.svg" />
                        <p dir="ltr" className={styles.mobileNumber}>
                          {t("lbl_customer_service")}
                        </p>
                      </div>
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
          {renderMenu}
          {isAuth && renderFooter()}
        </>
      )}
    </UserContext.Consumer>
  );
};
